import { Component, OnInit, OnDestroy, AfterViewInit, NgZone } from '@angular/core';
import { HttpService } from '../services/http-service';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { LocalStorageKeys } from '../helpers/constants';
import { PosthogService } from '../services/posthog.service';
import { GoogleAuthService, GoogleUser, GoogleAuthError } from '../services/google-auth.service';
import { Subscription } from 'rxjs';

/**
 * LoginHomeComponent
 * 
 * This component handles user authentication using Google Identity Services (GIS).
 * It provides three authentication methods in order of preference:
 * 
 * 1. One Tap Sign-In: Shows a native-looking prompt (best conversion)
 * 2. Google Sign-In Button: Rendered by Google, handles FedCM
 * 3. Redirect Flow: Fallback if needed
 * 
 * Key improvements over the old popup-based approach:
 * - No popup blockers
 * - Works with Safari ITP and enhanced privacy features
 * - Better UX that feels native to the browser
 * - Higher conversion rates
 */
@Component({
  selector: 'app-login-home',
  templateUrl: './login-home.component.html',
  styleUrls: ['./login-home.component.css'],
})
export class LoginHomeComponent implements OnInit, OnDestroy, AfterViewInit {
  providersReady: boolean = false;
  private hasProcessedLogin: boolean = false;
  loginInProgress: boolean = false;
  loginError: string = '';
  showErrorRetry: boolean = false;
  
  private subscriptions: Subscription[] = [];
  private oneTapShown: boolean = false;
  private buttonRendered: boolean = false;
  private renderRetryCount: number = 0;
  private readonly MAX_RENDER_RETRIES: number = 50; // 5 seconds max (50 * 100ms)

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    public httpService: HttpService,
    private googleAuth: GoogleAuthService,
    private posthog: PosthogService
  ) {}

  async ngOnInit() {
    console.log('[LoginHomeComponent] ====================================');
    console.log('[LoginHomeComponent] LOGIN PAGE LOADED (GIS Version)');
    console.log('[LoginHomeComponent] ====================================');
    console.log('[LoginHomeComponent] Timestamp:', new Date().toISOString());
    console.log('[LoginHomeComponent] Query params:', this.route.snapshot.queryParams);
    console.log('[LoginHomeComponent] User Agent:', navigator.userAgent);
    console.log('[LoginHomeComponent] URL:', window.location.href);

    // Check localStorage state
    const authProvider = localStorage.getItem(LocalStorageKeys.AuthProvider);
    console.log('[LoginHomeComponent] localStorage auth_provider:', authProvider);
    console.log('[LoginHomeComponent] httpService.loggedIn:', this.httpService.loggedIn);

    this.titleService.setTitle('Login');

    // PostHog: Track login page view with enhanced data
    this.posthog.captureEvent('login_page_viewed', {
      referrer: document.referrer,
      has_return_url: !!this.route.snapshot.queryParams['returnUrl'],
      auth_method: 'google_gis',
      device_type: this.getDeviceType(),
      browser: this.getBrowserInfo(),
    });

    // Subscribe to auth state changes
    const authSub = this.googleAuth.authState$.subscribe(user => {
      if (user && !this.hasProcessedLogin) {
        this.handleSuccessfulLogin(user);
      }
    });
    this.subscriptions.push(authSub);

    // Subscribe to ready state
    const readySub = this.googleAuth.ready$.subscribe(ready => {
      console.log('[LoginHomeComponent] GoogleAuth ready state:', ready);
      this.ngZone.run(() => {
        this.providersReady = ready;
        if (ready) {
          this.posthog.captureEvent('login_providers_ready', {
            method: 'google_gis',
          });
          
          // Render the Google button once ready
          if (!this.buttonRendered) {
            this.renderGoogleButton();
          }
        }
      });
    });
    this.subscriptions.push(readySub);

    // Subscribe to errors
    const errorSub = this.googleAuth.error$.subscribe(error => {
      this.handleAuthError(error);
    });
    this.subscriptions.push(errorSub);

    // Initialize Google Auth Service
    console.log('[LoginHomeComponent] Initializing GoogleAuthService...');
    const initialized = await this.googleAuth.initialize();
    console.log('[LoginHomeComponent] GoogleAuthService initialized:', initialized);
  }

  ngAfterViewInit() {
    // Give the DOM time to settle, then render button
    setTimeout(() => {
      if (this.providersReady && !this.buttonRendered) {
        this.renderGoogleButton();
      }
    }, 200);
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Cancel any pending One Tap prompt
    this.googleAuth.cancelOneTap();
  }

  /**
   * Render the Google Sign-In button
   * This uses Google's official button which handles FedCM and all auth flows
   */
  private renderGoogleButton(): void {
    console.log('[LoginHomeComponent] renderGoogleButton() called');

    const buttonContainer = document.getElementById('google-signin-button');
    if (!buttonContainer) {
      this.renderRetryCount++;
      if (this.renderRetryCount < this.MAX_RENDER_RETRIES) {
        console.log('[LoginHomeComponent] Button container not found, will retry (' + this.renderRetryCount + '/' + this.MAX_RENDER_RETRIES + ')');
        setTimeout(() => this.renderGoogleButton(), 100);
      } else {
        console.error('[LoginHomeComponent] Failed to render Google button after max retries');
        this.handleAuthError({ type: 'render_failed', reason: 'Max retries exceeded - button container not found' } as any);
      }
      return;
    }

    this.renderRetryCount = 0; // Reset on success
    this.buttonRendered = true;
    
    // Clear loading state and render actual button
    this.googleAuth.renderButton('google-signin-button', {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: 320,
      click_listener: () => {
        this.onGoogleButtonClick();
      }
    });
    
    console.log('[LoginHomeComponent] Google button rendered');
    
    // Show One Tap prompt after a short delay (only for returning users)
    // This gives users the choice of quick One Tap or button click
    setTimeout(() => {
      if (!this.oneTapShown && !this.hasProcessedLogin) {
        this.showOneTapPrompt();
      }
    }, 1500);
  }

  /**
   * Show One Tap sign-in prompt
   * This is the highest-converting method - shows a native prompt
   */
  private showOneTapPrompt(): void {
    console.log('[LoginHomeComponent] Showing One Tap prompt');
    this.oneTapShown = true;
    this.googleAuth.showOneTap();
  }

  /**
   * Handle Google button click
   */
  private onGoogleButtonClick(): void {
    console.log('[LoginHomeComponent] Google button clicked');
    
    this.posthog.captureEvent('login_attempt', {
      method: 'google_gis_button',
      timestamp: new Date().toISOString(),
    });
    
    // Clear any previous errors
    this.loginError = '';
    this.showErrorRetry = false;
    this.loginInProgress = true;
  }

  /**
   * Handle successful login
   */
  private handleSuccessfulLogin(user: GoogleUser): void {
    console.log('[LoginHomeComponent] handleSuccessfulLogin called', {
      userId: user.id,
      email: user.email,
      provider: user.provider,
    });
    
    this.hasProcessedLogin = true;
    this.loginInProgress = false;
    this.loginError = '';
    
    // Store auth provider
    localStorage.setItem(LocalStorageKeys.AuthProvider, user.provider);
    
    // Set user data in httpService (compatibility with existing code)
    this.httpService.socialUser = {
      id: user.id,
      email: user.email,
      name: user.name,
      firstName: user.firstName,
      lastName: user.lastName,
      photoUrl: user.photoUrl,
      provider: user.provider,
      idToken: user.idToken,
      authToken: user.authToken || '',
      authorizationCode: '',
      response: {}
    } as any;
    this.httpService.loggedIn = true;
    this.httpService.userId = user.id;
    
    // PostHog: Identify user and track successful login
    this.posthog.identifyUser(user.id, {
      email: user.email,
      name: user.name,
      provider: user.provider,
      login_date: new Date().toISOString(),
    });
    
    // Check if this is user's first login
    const posthogInstance = this.posthog.getPosthogInstance();
    if (posthogInstance) {
      const hasLoggedInBefore = posthogInstance.get_property('has_logged_in');
      
      if (!hasLoggedInBefore) {
        console.log('[LoginHomeComponent] First-time login detected for user:', user.id);
        this.posthog.captureEvent('user_registered_first_time', {
          method: 'google_gis',
          email: user.email,
          registration_date: new Date().toISOString(),
        });
        this.posthog.setSuperProperty('has_logged_in', true);
      }
    }
    
    // Navigate to return URL or home
    const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    console.log('[LoginHomeComponent] Navigation target:', returnUrl);
    
    this.ngZone.run(() => {
      this.router.navigateByUrl(returnUrl).then(() => {
        console.log('[LoginHomeComponent] Navigation completed successfully');
      }).catch((error) => {
        console.error('[LoginHomeComponent] Navigation failed:', error);
      });
    });
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: GoogleAuthError): void {
    console.error('[LoginHomeComponent] Auth error:', error);
    
    this.ngZone.run(() => {
      this.loginInProgress = false;
      
      let userFriendlyMessage = '';
      let showRetry = true;
      
      switch (error.type) {
        case 'user_cancelled':
          userFriendlyMessage = this.httpService.getTransation().LoginCancelled || 'Sign in was cancelled. Please try again.';
          showRetry = true;
          break;
        case 'one_tap_failed':
          // Don't show error for One Tap failures - button is still available
          console.log('[LoginHomeComponent] One Tap failed, button available as fallback');
          return;
        case 'script_load_failed':
          userFriendlyMessage = this.httpService.getTransation().LoginNetworkError || 'Could not connect to Google. Please check your internet connection.';
          showRetry = true;
          break;
        case 'token_invalid':
          userFriendlyMessage = this.httpService.getTransation().LoginAuthError || 'Authentication failed. Please try again.';
          showRetry = true;
          break;
        default:
          userFriendlyMessage = this.httpService.getTransation().LoginGenericError || 'Something went wrong. Please try again.';
          showRetry = true;
      }
      
      this.loginError = userFriendlyMessage;
      this.showErrorRetry = showRetry;
      
      // PostHog: Track login failure
      this.posthog.captureEvent('login_failed', {
        method: 'google_gis',
        error_type: error.type,
        error_reason: error.reason,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        browser: this.getBrowserInfo(),
        device_type: this.getDeviceType(),
      });
    });
  }

  /**
   * Retry login after error
   */
  retryLogin(): void {
    console.log('[LoginHomeComponent] Retry login clicked');
    this.loginError = '';
    this.showErrorRetry = false;
    this.oneTapShown = false;
    
    // Re-show One Tap
    setTimeout(() => this.showOneTapPrompt(), 500);
  }

  /**
   * Sign out
   */
  signOut(): void {
    this.posthog.captureEvent('logout');
    this.posthog.reset();
    this.googleAuth.signOut();
    this.httpService.loggedIn = false;
    this.httpService.socialUser = null;
    this.httpService.userId = null;
    this.hasProcessedLogin = false;
  }

  private getBrowserInfo(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('MSIE') || ua.includes('Trident/')) return 'Internet Explorer';
    return 'Unknown';
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }
}
